<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  NConfigProvider,
  darkTheme,
  lightTheme,
  NLayout,
  NLayoutHeader,
  NSpace,
  NButton,
  NDropdown,
  NH1,
  NSwitch,
  NText,
  type DropdownOption,
} from 'naive-ui';
import { GridLayout, GridItem } from 'vue3-grid-layout';
import {
  ClockWidget,
  WorldClockWidget,
  TimerWidget,
  StopwatchWidget,
  AlarmWidget,
  useWidgets,
} from '@awesomeclocks/ui';
import type { WidgetType } from '@awesomeclocks/types';

const {
  widgets,
  addWidget,
  removeWidget,
  updateWidgetName,
  updateWidgetPosition,
  updateWidgetSettings,
  updateWidgetState,
  clearAllWidgets,
} = useWidgets();

const isDark = ref(true);
const theme = computed(() => (isDark.value ? darkTheme : lightTheme));

const widgetTypeOptions: DropdownOption[] = [
  {
    label: '🕐 Clock',
    key: 'clock',
  },
  {
    label: '🌍 World Clock',
    key: 'world-clock',
  },
  {
    label: '⏲️ Timer',
    key: 'timer',
  },
  {
    label: '⏱️ Stopwatch',
    key: 'stopwatch',
  },
  {
    label: '⏰ Alarm',
    key: 'alarm',
  },
];

const handleAddWidget = (type: string) => {
  const widgetNames: Record<string, string> = {
    clock: 'Clock',
    'world-clock': 'World Clock',
    timer: 'Timer',
    stopwatch: 'Stopwatch',
    alarm: 'Alarm',
  };

  addWidget({
    type: type as WidgetType,
    name: widgetNames[type] || 'Widget',
  });
};

const layout = computed(() => {
  return widgets.value.map((widget) => ({
    i: widget.id,
    x: widget.position.x,
    y: widget.position.y,
    w: widget.position.w,
    h: widget.position.h,
  }));
});

const updateLayout = (
  newLayout: Array<{ i: string; x: number; y: number; w: number; h: number }>
) => {
  newLayout.forEach((item) => {
    const widget = widgets.value.find((candidate) => candidate.id === item.i);
    if (!widget) {
      return;
    }

    const { x, y, w, h } = widget.position;
    if (x === item.x && y === item.y && w === item.w && h === item.h) {
      return;
    }

    updateWidgetPosition(item.i, {
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    });
  });
};

const dragIgnoreFrom = [
  '.no-drag',
  'input',
  'textarea',
  'button',
  '.n-input',
  '.n-input__input',
  '.n-input__textarea',
  '.n-base-selection',
  '.n-base-selection-input',
  '.n-switch',
  '.n-button',
].join(', ');

// Responsive breakpoints for the grid layout
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const cols = { lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 };
</script>

<template>
  <NConfigProvider :theme="theme">
    <NLayout style="min-height: 100vh">
      <NLayoutHeader
        bordered
        style="
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        "
      >
        <NH1 style="margin: 0">⏰ Awesome Clocks</NH1>

        <NSpace>
          <NDropdown :options="widgetTypeOptions" @select="handleAddWidget">
            <NButton type="primary">+ Add Widget</NButton>
          </NDropdown>

          <NButton :disabled="widgets.length === 0" @click="clearAllWidgets">
            Clear All
          </NButton>

          <NSpace align="center">
            <NText>🌞</NText>
            <NSwitch v-model:value="isDark" />
            <NText>🌙</NText>
          </NSpace>
        </NSpace>
      </NLayoutHeader>

      <div style="padding: 24px">
        <div v-if="widgets.length === 0" class="empty-state">
          <NH1>Welcome to Awesome Clocks! 🎉</NH1>
          <NText :depth="2">
            Click "Add Widget" above to get started with your customizable clock
            dashboard.
          </NText>
        </div>

        <GridLayout
          v-else
          :layout="layout"
          :col-num="12"
          :row-height="60"
          :is-draggable="true"
          :is-resizable="true"
          :vertical-compact="true"
          :margin="[10, 10]"
          :use-css-transforms="true"
          :responsive="true"
          :breakpoints="breakpoints"
          :cols="cols"
          @layout-updated="updateLayout"
        >
          <GridItem
            v-for="widget in widgets"
            :key="widget.id"
            :x="widget.position.x"
            :y="widget.position.y"
            :w="widget.position.w"
            :h="widget.position.h"
            :i="widget.id"
            :drag-ignore-from="dragIgnoreFrom"
          >
            <ClockWidget
              v-if="widget.type === 'clock'"
              :widget="widget"
              @remove="removeWidget(widget.id)"
              @update-name="updateWidgetName(widget.id, $event)"
              @update-settings="updateWidgetSettings(widget.id, $event)"
            />
            <WorldClockWidget
              v-else-if="widget.type === 'world-clock'"
              :widget="widget"
              @remove="removeWidget(widget.id)"
              @update-name="updateWidgetName(widget.id, $event)"
              @update-settings="updateWidgetSettings(widget.id, $event)"
            />
            <TimerWidget
              v-else-if="widget.type === 'timer'"
              :widget="widget"
              @remove="removeWidget(widget.id)"
              @update-name="updateWidgetName(widget.id, $event)"
              @update-settings="updateWidgetSettings(widget.id, $event)"
              @update-state="$event && updateWidgetState(widget.id, $event)"
            />
            <StopwatchWidget
              v-else-if="widget.type === 'stopwatch'"
              :widget="widget"
              @remove="removeWidget(widget.id)"
              @update-name="updateWidgetName(widget.id, $event)"
              @update-settings="updateWidgetSettings(widget.id, $event)"
              @update-state="$event && updateWidgetState(widget.id, $event)"
            />
            <AlarmWidget
              v-else-if="widget.type === 'alarm'"
              :widget="widget"
              @remove="removeWidget(widget.id)"
              @update-name="updateWidgetName(widget.id, $event)"
              @update-settings="updateWidgetSettings(widget.id, $event)"
            />
          </GridItem>
        </GridLayout>
      </div>
    </NLayout>
  </NConfigProvider>
</template>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  gap: 16px;
}
</style>

<style>
/* Vue Grid Layout Styles */
.vue-grid-layout {
  position: relative;
  background: transparent;
}

.vue-grid-item {
  position: absolute;
  transition: all 200ms ease;
  transition-property: left, top, right;
  box-sizing: border-box;
}

.vue-grid-item.resizing {
  transition: none;
  z-index: 3;
}

.vue-grid-item.static {
  background: transparent;
}

.vue-grid-item .resizing {
  opacity: 0.9;
}

.vue-grid-item .static {
  background: transparent;
}

.vue-grid-item .text {
  font-size: 24px;
  text-align: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 100%;
  width: 100%;
}

.vue-grid-item .no-drag {
  height: 100%;
  width: 100%;
}

.vue-grid-item .minMax {
  font-size: 12px;
}

.vue-grid-item .add {
  cursor: pointer;
}

.vue-draggable-handle {
  position: absolute;
  width: 20px;
  height: 20px;
  top: 0;
  left: 0;
  background: transparent;
  cursor: move;
}
</style>
